import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/store.js';
import { useInvalidate } from '../lib/queries.js';
import { Spinner, Note } from './ui.js';
import { num } from '../lib/format.js';
import { useT } from '../lib/i18n.js';

/**
 * Werkbank für die Entwicklung, Tarif und Guthaben ohne Umweg über Stripe.
 *
 * Beim Bauen der Oberfläche braucht man ständig einen anderen Tarif: Die
 * Paywall zeigt sich nur unter Free, Vollautomatik erst ab Creator,
 * Team-Zugänge ab Studio. Ohne diesen Knopf hieße das jedes Mal ein
 * Stripe-Testkauf oder ein Eingriff von Hand in die Datenbank.
 *
 * Drei Schlösser halten das aus dem Livebetrieb heraus:
 *
 *   1. `import.meta.env.DEV`, im gebauten Bundle ist der Zweig tot und
 *      fliegt beim Tree-Shaking komplett raus. Der Code existiert dort nicht.
 *   2. Der Server montiert `/api/dev` nur, wenn NODE_ENV nicht „production"
 *      ist. Dort gibt es die Routen schlicht nicht.
 *   3. Der Server nimmt Aufrufe nur von localhost an.
 *
 * Jedes einzelne davon würde reichen. Zusammen ist es keine Frage mehr.
 */

interface DevState {
  plan: string;
  planName: string;
  subscriptionStatus: string;
  creditBalance: number;
  available: number;
  plans: {
    key: string;
    name: string;
    maxProjects: number;
    maxAccountsPerProject: number;
    monthlyCredits: number;
  }[];
}

export function DevTools() {
  // Der Aufruf steht bewusst zuerst: Im Produktionsbundle bleibt vom Rest
  // nichts übrig, weil der Bundler den ganzen Zweig als unerreichbar erkennt.
  if (!import.meta.env.DEV) return null;
  return <DevPanel />;
}

function DevPanel() {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<DevState | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refreshOrg = useAuth((s) => s.refreshOrg);
  const org = useAuth((s) => s.org);
  const toast = useAuth((s) => s.toast);
  const invalidate = useInvalidate();
  const t = useT();

  async function load() {
    try {
      setState(await api.get<DevState>('/dev/state'));
      setError(null);
    } catch (err) {
      // Läuft der Server nicht im Entwicklungsmodus, gibt es die Routen nicht.
      setError(err instanceof ApiError ? err.message : t('Werkbank nicht erreichbar.'));
    }
  }

  useEffect(() => { if (open) void load(); }, [open]);

  /** Nach jeder Änderung muss die ganze Oberfläche den neuen Stand sehen. */
  async function sync() {
    await refreshOrg();
    await invalidate.billing();
    await invalidate.projects();
    await load();
  }

  async function setPlan(plan: string, withCredits: boolean) {
    setBusy(plan);
    try {
      const res = await api.post<{ planName: string }>('/dev/plan', { plan, withCredits });
      await sync();
      toast(t('Tarif steht jetzt auf {plan}.', { plan: res.planName }), 'ok');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('Ging nicht'), 'err');
    } finally {
      setBusy(null);
    }
  }

  async function addCredits(credits: number) {
    setBusy(`credits:${credits}`);
    try {
      await api.post('/dev/credits', { credits });
      await sync();
      toast(
        credits > 0
          ? t('{n} Credits gutgeschrieben.', { n: num(credits) })
          : t('{n} Credits abgezogen.', { n: num(-credits) }),
        'ok',
      );
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('Ging nicht'), 'err');
    } finally {
      setBusy(null);
    }
  }

  async function unreserve() {
    setBusy('unreserve');
    try {
      await api.post('/dev/unreserve');
      await sync();
      toast(t('Reservierungen freigeräumt.'), 'ok');
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t('Ging nicht'), 'err');
    } finally {
      setBusy(null);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={t('Entwickler-Werkbank, nur lokal')}
        className="devtools-fab"
      >
        <span className="dot dot-ok" />
        Dev · {org?.planName ?? org?.plan ?? '—'}
      </button>
    );
  }

  return (
    <div className="card pop devtools">
      <div className="row devtools-head">
        <span className="dot dot-ok" />
        <div className="grow">
          <div style={{ fontSize: 13, fontWeight: 600 }}>{t('Entwickler-Werkbank')}</div>
          <div className="dim" style={{ fontSize: 11.5 }}>{t('nur lokal · nicht im Livebetrieb')}</div>
        </div>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(false)} aria-label={t('Schließen')}>✕</button>
      </div>

      <div className="col" style={{ padding: 12, gap: 14 }}>
        {error && (
          <Note tone="warn">{error}</Note>
        )}

        <div>
          <div className="section-label" style={{ marginBottom: 7 }}>{t('Tarif')}</div>
          <div className="col" style={{ gap: 5 }}>
            {(state?.plans ?? []).map((p) => {
              const active = state?.plan === p.key;
              return (
                <button
                  type="button"
                  key={p.key}
                  onClick={() => void setPlan(p.key, p.key !== 'free')}
                  disabled={!!busy}
                  className={`devtools-plan ${active ? 'devtools-plan-on' : ''}`}
                >
                  {busy === p.key ? <Spinner size={11} /> : (
                    <span className="devtools-plan-mark">{active ? '●' : '○'}</span>
                  )}
                  <span className="grow" style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</span>
                  <span className="dim" style={{ fontSize: 11.5 }}>
                    {p.maxProjects} {p.maxProjects === 1 ? t('Projekt') : t('Projekte')}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="dim" style={{ fontSize: 12, marginTop: 7, lineHeight: 1.45 }}>
            {t('Bezahlte Tarife schreiben ihr Monatsguthaben gleich mit gut.')}
          </div>
        </div>

        <div>
          <div className="row" style={{ marginBottom: 7 }}>
            <span className="section-label grow">{t('Guthaben')}</span>
            <span className="mono" style={{ fontSize: 12, fontWeight: 500 }}>
              {num(state?.available ?? 0)}
            </span>
          </div>
          <div className="row wrap" style={{ gap: 5 }}>
            {[1000, 10_000, 100_000].map((amount) => (
              <button
                type="button"
                key={amount}
                className="btn btn-sm btn-secondary"
                onClick={() => void addCredits(amount)}
                disabled={!!busy}
              >
                {busy === `credits:${amount}` && <Spinner size={10} />} +{num(amount)}
              </button>
            ))}
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => void addCredits(-(state?.creditBalance ?? 0))}
              disabled={!!busy || !state?.creditBalance}
              title={t('Guthaben auf null setzen, um leere Kassen zu testen')}
            >
              {t('auf 0')}
            </button>
          </div>
        </div>

        <div>
          <div className="section-label" style={{ marginBottom: 7 }}>{t('Steckengeblieben?')}</div>
          <button type="button" className="btn btn-sm btn-ghost btn-block" onClick={() => void unreserve()} disabled={!!busy}>
            {busy === 'unreserve' && <Spinner size={10} />} {t('Reservierungen freiräumen')}
          </button>
          <div className="dim" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.45 }}>
            {t('Abgebrochene Läufe lassen Credits geblockt zurück. Das gibt sie wieder frei.')}
          </div>
        </div>
      </div>
    </div>
  );
}
